import * as React from 'react';
import { observer } from 'mobx-react';
import {
    BasicFormWithAlerts,
    CustomFormItem,
    IconButton,
    IconTextButton,
    Label,
    PasswordFieldItem,
    SimpleFileUploadItem,
    Skeleton,
    TextFieldItem,
    useForm,
} from '../../../../ui-lib';
import { useAccountConfigurationStore, useAccountStore } from '../../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { exportFile, getErrorMessage, isNullOrEmpty } from '../../../../common-utils';
import { confirmSave } from '../utils';
import { useAlertMessages } from '../../components/alert/use-messages';
import { useSimpleFileUploadProps } from 'ui-lib/components/forms/items/use-simple-file-upload-item';
import { useSpinnerComponent } from 'pages/private/components/loading-component';
import { isStrictUrl, sleep } from 'utils/common-utils';
import { ExternalRepositoryDto } from 'dto/account-configuration.dto';

interface ILoginCredentials {
    repositoryURL?: string;
    username?: string;
    password?: string;
}
export const ExternalRepoSettings = observer(() => {
    const accountStore = useAccountStore();
    const accountConfigurationStore = useAccountConfigurationStore();
    const { id } = useParams();
    const [form] = useForm();
    const navigate = useNavigate();
    const account = React.useMemo(() => accountStore.getAccountById(parseInt(id)), [id]);
    const [repoSettings, setRepoSettings] = React.useState<ExternalRepositoryDto>(null);
    const [loading, setLoading] = React.useState<boolean>(false);

    const { SpinnerComponent, onStart, onFinish } = useSpinnerComponent();
    const { errorMessages, addErrorMessages, clearErrorMessages } = useAlertMessages();
    const [settings, dispatch] = React.useReducer<React.Reducer<ILoginCredentials, { type: string; payload: string }>>(
        (state, action) => {
            switch (action.type) {
                case 'repositoryUrl': {
                    form.setFieldsValue({ repositoryUrl: action.payload });
                    return { ...state, repositoryURL: action.payload };
                }
                case 'username': {
                    form.setFieldsValue({ username: action.payload });
                    return { ...state, username: action.payload };
                }
                case 'password': {
                    form.setFieldsValue({ password: action.payload });
                    return { ...state, password: action.payload };
                }
                default:
                    return state;
            }
        },
        {
            repositoryURL: '',
            username: '',
            password: '',
        },
    );

    React.useEffect(() => {
        if (!account) return;
        (async function getRepoSettings(): Promise<void> {
            try {
                clearErrorMessages();
                setLoading(true);
                const repoSettings = await accountConfigurationStore.getExternalRepoSettings(account.id, account.uuid);
                setRepoSettings(repoSettings);
            } catch (err) {
                addErrorMessages(getErrorMessage(err));
            } finally {
                setLoading(false);
            }
        })();
    }, [account]);

    const simpleFileUploadProps = useSimpleFileUploadProps({
        uploadButton: {
            label: 'Upload GPG Key',
            type: 'default',
        },
        multiple: false,
        afterFileListChange: (): void => {
            form.validateFields(['gpgKey']);
        },
    });

    const settingsChanged = React.useMemo(() => {
        if (!repoSettings) return true;
        return (
            repoSettings.repositoryURL !== settings.repositoryURL ||
            repoSettings.username !== settings.username ||
            repoSettings.password !== settings.password ||
            !isNullOrEmpty(simpleFileUploadProps.files)
        );
    }, [repoSettings, settings, simpleFileUploadProps.files]);

    React.useEffect(() => {
        if (!repoSettings) return;
        dispatch({ type: 'repositoryUrl', payload: repoSettings.repositoryURL });
        dispatch({ type: 'username', payload: repoSettings.username });
        dispatch({ type: 'password', payload: repoSettings.password });
    }, [repoSettings]);

    const onSave = async (): Promise<void> => {
        clearErrorMessages();
        onStart();
        try {
            if (
                !settings.repositoryURL ||
                !settings.username ||
                !settings.password
            ) {
                addErrorMessages('All fields are required');
                return;
            }
            if (repoSettings.isEmpty) {
                if (isNullOrEmpty(simpleFileUploadProps.files)) {
                    addErrorMessages('GPG Key is required');
                    return;
                }
                await accountConfigurationStore.createExternalRepoSettings(account.id, account.uuid, {
                    repositoryURL: settings.repositoryURL,
                    username: settings.username,
                    password: settings.password,
                    file: simpleFileUploadProps.files[0]
                });
            } else {
                await accountConfigurationStore.updateExternalRepoSettings(account.id, account.uuid, {
                    repositoryURL: settings.repositoryURL,
                    username: settings.username,
                    password: settings.password,
                    file: simpleFileUploadProps.files[0]
                });
            }
            navigate('/admin/accounts');
        } catch (err) {
            addErrorMessages(getErrorMessage(err));
        } finally {
            onFinish();
        }
    };

    return (
        <Skeleton loading={loading}>
            <SpinnerComponent
                child={
                    <BasicFormWithAlerts
                        form={form}
                        cardPros={{ title: account?.name ?? 'External Repository Settings' }}
                        errorMessages={errorMessages}
                        onSubmit={(): void =>
                            confirmSave(
                                undefined,
                                'This action will trigger data transfer and GPG jobs on all devices to apply the new configuration. Do you want to save your change?',
                                onSave,
                            )
                        }
                        items={[
                            <TextFieldItem
                                key='repositoryUrl'
                                label='Repository URL'
                                code='repositoryUrl'
                                placeholder='Repository URL'
                                labelAlign='right'
                                isRequired={true}
                                onChange={(e):void => dispatch({ type: 'repositoryUrl', payload: e.target.value })}
                                validator={(rule, value, callback): void => {
                                    if (isNullOrEmpty(value)) return callback(undefined);
                                    callback(isStrictUrl(value) ? undefined: 'The repository URL should be in a valid format.');
                                }}
                                initialValue={settings.repositoryURL}
                            />,
                            <TextFieldItem
                                key='username'
                                label='User name'
                                code='username'
                                placeholder='User'
                                isRequired={true}
                                labelAlign='right'
                                onChange={(e): void => dispatch({ type: 'username', payload: e.target.value })}
                                initialValue={settings.username}
                            />,
                            <PasswordFieldItem
                                key='password'
                                label='Password'
                                code='password'
                                visibilityToggle={true}
                                isRequired={true}
                                labelAlign='right'
                                onChange={(e): void => dispatch({ type: 'password', payload: e.target.value })}
                                initialValue={settings.password}
                            />,
                            ...(repoSettings?.gpgFileName
                                ? [
                                      <CustomFormItem
                                          key='currentGpgKeyDownload'
                                          label='Current GPG Key'
                                          code='currentGpgKeyDownload'
                                          labelAlign='right'
                                      >
                                          <div style={{ display: 'flex'}}>
                                              <Label label={repoSettings.gpgFileName} italic={true} style={{ margin: '5px' }}/>
                                              <IconButton
                                                  iconName='download'
                                                  type='link'
                                                  onClick={(): void => {
                                                      exportFile(
                                                          repoSettings.gpgFileName,
                                                          repoSettings.gpgContent,
                                                          'text/plain',
                                                      );
                                                  }}
                                              />
                                          </div>
                                      </CustomFormItem>,
                                  ]
                                : []),
                            <SimpleFileUploadItem
                                key='gpgKey'
                                code='gpgKey'
                                label='GPG Key'
                                labelAlign='right'
                                validator={(rule, value, callback): void => {
                                    if (!repoSettings.isEmpty) {
                                        return callback(undefined);
                                    }
                                    if (simpleFileUploadProps.files.length === 0) {
                                        return callback('GPG Key is required');
                                    }
                                    callback(undefined);
                                }}
                                simpleFileUploadProps={simpleFileUploadProps.getFileUploadProps()}
                            />,
                            <div key='buttons' style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <IconTextButton
                                    key='cancel-btn'
                                    onClick={(): void => navigate('/admin/accounts')}
                                    label='Cancel'
                                />
                                <IconTextButton
                                    key='save-btn'
                                    type='primary'
                                    htmlType='submit'
                                    disabled={!settingsChanged}
                                    label='Save'
                                />
                            </div>,
                        ]}
                    />
                }
            />
        </Skeleton>
    );
});
